import React, { useMemo, useState } from 'react';
import { Box } from '@material-ui/core';
import { VariantType } from 'notistack';
import { CustomDashedButton } from '../../../custom-forms';
import { useStyles, cx } from '../../../custom-forms';
import {
  parseAlternativePartsFile,
  parseOriginalPartsFile
} from '../utils/alternative-calculation.import';
import {
  ALTERNATIVE_PARTS_IMPORT_EXAMPLE,
  ALTERNATIVE_PARTS_IMPORT_TEMPLATE,
  ORIGINAL_PARTS_IMPORT_EXAMPLE,
  ORIGINAL_PARTS_IMPORT_TEMPLATE
} from '../../../helpers/Constants';
import { CaseCalculationPart } from '../../../interfaces/Case';
import { WorkflowCalculation, WorkflowSupplier } from '../../../interfaces/Workflow';
import { Organization } from '../../../interfaces/Organization';
import { IReferential } from '../../../interfaces/IReferential';
import {
  CalculationActions,
  ColumnFilter,
  ColumnKey,
  FiltersState,
  SortState
} from '../types/alternative-calculation.types';
import { useTranslate } from '../../../hooks/useTranslate';
import { useAppSelector } from '../../../store/configureStore';
import { useVariantsEnabled, useCaseIsOffline } from '../context/CalculationFlagsContext';
import { buildAltCalcGridColumns } from '../config/altCalcGridColumns';
import { filterOptionLabel, filterOptionsFor } from '../utils/calculation.utils';
import AlternativeCalculationChips from './AlternativeCalculationChips';
import AlternativeCalculationPiecesHeader from './AlternativeCalculationPiecesHeader';
import AlternativeCalculationPart from './AlternativeCalculationPart';
import CalculationPartAddDialog from './CalculationPartAddDialog';
import AlternativeCalculationImportDialog from './AlternativeCalculationImportDialog';

export interface AlternativeCalculationPiecesProps {
  parts: CaseCalculationPart[];
  sort: SortState;
  filters: FiltersState;
  workflowCalculation: WorkflowCalculation | null;
  partQualityTypes: IReferential[];
  supplierList: Organization[];
  nonStockSuppliers: WorkflowSupplier[];
  isAddDialogOpen: boolean;
  isAddDialog: boolean;
  newCalculationPart: CaseCalculationPart;
  actions: CalculationActions;
  onNotify: (message: string, variant: VariantType) => void;
  onSort: (key: ColumnKey) => void;
  onApplyFilter: (key: ColumnKey, value: ColumnFilter | null) => void;
  onRemoveFilter: (key: ColumnKey) => void;
  onClearFilters: () => void;
  onUnselectAll: () => void;
  onCloseAddDialog: () => void;
  onSubmitCalculationPart: (part: CaseCalculationPart) => void;
}

const AlternativeCalculationPieces: React.FC<AlternativeCalculationPiecesProps> = ({
  parts,
  sort,
  filters,
  workflowCalculation,
  partQualityTypes,
  supplierList,
  nonStockSuppliers,
  isAddDialogOpen,
  isAddDialog,
  newCalculationPart,
  actions,
  onNotify,
  onSort,
  onApplyFilter,
  onRemoveFilter,
  onClearFilters,
  onUnselectAll,
  onCloseAddDialog,
  onSubmitCalculationPart
}) => {
  const c = useStyles();
  const t = useTranslate();
  const country = useAppSelector((s) => s.app?.appUser?.organization?.country ?? null);
  const variantsEnabled = useVariantsEnabled();
  const caseIsOffline = useCaseIsOffline();
  const [importKind, setImportKind] = useState<'original' | 'alternative' | null>(null);

  const altColumns = useMemo(
    () =>
      buildAltCalcGridColumns({
        workflowCalculation,
        qualities: partQualityTypes,
        actions,
        onCopyPart: actions.copyCalculationPart,
        onNotify,
        c,
        t,
        country,
        variantsEnabled,
        showNetPrice: workflowCalculation?.alternativeNetPrice ?? false,
        caseIsOffline,
        nonStockSuppliers
      }),
    [nonStockSuppliers]
  );

  const supplierOptions = useMemo<Organization[]>(() => {
    const seen: { [name: string]: true } = {};
    const list: Organization[] = [];
    const add = (org: Organization | null) => {
      if (org && org.name && !seen[org.name]) {
        seen[org.name] = true;
        list.push(org);
      }
    };
    supplierList.forEach((supplier) => add(supplier));
    parts.forEach((p) => p.alternativeParts.forEach((a) => add(a.supplier)));
    return list;
  }, [parts, supplierList]);

  const importOriginalParts = async (file: File) => {
    try {
      actions.importOriginalParts(await parseOriginalPartsFile(file, t, partQualityTypes));
      onNotify(t('ALT_CALC_IMPORT_OK', 'Import realizat cu succes'), 'success');
    } catch (err) {
      onNotify((err as Error).message, 'error');
    }
  };

  const importAlternativeParts = async (file: File) => {
    const partIdByCode: { [code: string]: number } = {};
    parts.forEach((p) => {
      if (p.guideNumber) partIdByCode[p.guideNumber] = p.id;
    });
    if (!Object.keys(partIdByCode).length) {
      onNotify(t('NO_ORIGINAL_PARTS_FOUND', 'Nu există nicio piesă originală!'), 'error');
      return;
    }
    try {
      const alternativeParts = await parseAlternativePartsFile(
        file,
        partIdByCode,
        supplierOptions,
        t,
        partQualityTypes
      );
      actions.importAlternativeParts(alternativeParts);
      onNotify(t('ALT_CALC_IMPORT_OK', 'Import realizat cu succes'), 'success');
    } catch (err) {
      onNotify((err as Error).message, 'error');
    }
  };

  return (
    <Box>
      <AlternativeCalculationChips
        filters={filters}
        onRemove={onRemoveFilter}
        onClearAll={onClearFilters}
      />

      <Box style={{ overflowX: 'auto' }}>
        <Box style={{ minWidth: 'min-content' }}>
          <AlternativeCalculationPiecesHeader
            sort={sort}
            filters={filters}
            onSort={onSort}
            onApplyFilter={onApplyFilter}
            filterOptionsFor={(key) => filterOptionsFor(parts, key, filters)}
            filterOptionLabel={filterOptionLabel}
            onUnselectAll={onUnselectAll}
            hasUnselectAllColumn={true}
            hasRecoColumn={true}
            columns={altColumns}
            flush={false}
          />

          {parts.map((part) => (
            <AlternativeCalculationPart
              key={part.id}
              part={part}
              sort={sort}
              filters={filters}
              suppliers={supplierList}
              qualities={partQualityTypes}
              workflowCalculation={workflowCalculation}
              actions={actions}
              onNotify={onNotify}
              onCopyPart={actions.copyCalculationPart}
              enableLineFilterReset={true}
              alternativePartsHeight={320}
              infoTooltip={null}
              columns={altColumns}
              canExpand={true}
              flush={false}
              variantsHeader={null}
            />
          ))}

          {parts.length === 0 ? (
            <Box className={c.emptyRow}>
              {t('NO_RECORDS_TO_DISPLAY', 'Nu exista inregistrari de afisat')}
            </Box>
          ) : null}
        </Box>
      </Box>

      <Box className={cx(c.listFoot, c.row, c.wrap, c.gap12)}>
        {workflowCalculation?.addOriginalPart ? (
          <CustomDashedButton icon="plus" onClick={actions.addCalculationPart}>
            {t('ALT_CALC_ADD_PART', 'Adaugă cod pentru interogare')}
          </CustomDashedButton>
        ) : null}
        {workflowCalculation?.importOriginalParts ? (
          <CustomDashedButton icon="upload" onClick={() => setImportKind('original')}>
            {t(
              'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_IMPORT_NEW_PARTS_LABEL',
              'Importă piese noi'
            )}
          </CustomDashedButton>
        ) : null}
        {workflowCalculation?.importAlternativeParts ? (
          <CustomDashedButton
            icon="upload"
            className={c.pushRight}
            onClick={() => setImportKind('alternative')}
          >
            {t(
              'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_IMPORT_ALTERNATIVE_PARTS_LABEL',
              'Importă piese alternative'
            )}
          </CustomDashedButton>
        ) : null}
      </Box>

      {isAddDialogOpen ? (
        <CalculationPartAddDialog
          open
          initialPart={newCalculationPart}
          qualities={partQualityTypes}
          isAdd={isAddDialog}
          onClose={onCloseAddDialog}
          onSave={onSubmitCalculationPart}
        />
      ) : null}

      <AlternativeCalculationImportDialog
        open={importKind === 'original'}
        title={t(
          'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_IMPORT_NEW_PARTS_LABEL',
          'Importă piese noi'
        )}
        templateHeaders={ORIGINAL_PARTS_IMPORT_TEMPLATE}
        templateExample={ORIGINAL_PARTS_IMPORT_EXAMPLE}
        templateName={t('ALT_CALC_IMPORT_ORIGINAL_TEMPLATE_NAME', 'Model_import_piese_originale')}
        onClose={() => setImportKind(null)}
        onSubmit={importOriginalParts}
      />

      <AlternativeCalculationImportDialog
        open={importKind === 'alternative'}
        title={t(
          'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_IMPORT_ALTERNATIVE_PARTS_LABEL',
          'Importă piese alternative'
        )}
        templateHeaders={ALTERNATIVE_PARTS_IMPORT_TEMPLATE}
        templateExample={ALTERNATIVE_PARTS_IMPORT_EXAMPLE}
        templateName={t(
          'ALT_CALC_IMPORT_ALTERNATIVE_TEMPLATE_NAME',
          'Model_import_piese_alternative'
        )}
        onClose={() => setImportKind(null)}
        onSubmit={importAlternativeParts}
      />
    </Box>
  );
};

export default AlternativeCalculationPieces;
